import { createId } from '@/core/id';
import { chatError } from '@/core/types';
/**
 * WebSocket adapter with request/response correlation over a single socket,
 * auto-reconnect with exponential backoff, and an outbox that survives
 * short disconnects.
 *
 * The server contract this expects (override `parseFrame` to change it):
 *   client -> { id, type: 'conversation.create' | 'message.send' | ..., payload }
 *   server -> { id?, type: 'message.delta' | 'message.complete' | 'typing' | ..., ... }
 */
export class WsTransport {
    options;
    name = 'websocket';
    capabilities = {
        streaming: true,
        push: true,
        history: true,
        feedback: true,
        handoff: true,
    };
    socket;
    emit;
    pending = new Map();
    outbox = [];
    reconnectAttempts = 0;
    heartbeat;
    disposed = false;
    constructor(options) {
        this.options = options;
    }
    async connect(emit) {
        this.emit = emit;
        await this.open();
    }
    async createConversation(input) {
        const raw = (await this.rpc('conversation.create', input));
        return {
            id: String(raw.id ?? createId('conv')),
            createdAt: Number(raw.createdAt ?? Date.now()),
            status: raw.status ?? 'open',
        };
    }
    async loadHistory(conversationId) {
        const raw = (await this.rpc('conversation.history', { conversationId }));
        return raw.messages ?? [];
    }
    async sendMessage({ conversationId, message, replyId, context }, emit, signal) {
        this.emit = emit;
        // Deltas arrive as unsolicited frames keyed by replyId, so this only
        // needs to fire-and-forget; completion is signalled by the server.
        this.send({
            id: createId('req'),
            type: 'message.send',
            payload: { conversationId, content: message.content, messageId: message.id, replyId, context },
        });
        signal.addEventListener('abort', () => this.send({ id: createId('req'), type: 'message.cancel', payload: { replyId } }), { once: true });
    }
    async sendFeedback(input) {
        this.send({ id: createId('req'), type: 'message.feedback', payload: input });
    }
    async requestHandoff(conversationId, reason) {
        this.send({ id: createId('req'), type: 'conversation.handoff', payload: { conversationId, reason } });
    }
    dispose() {
        this.disposed = true;
        clearInterval(this.heartbeat);
        this.socket?.close(1000, 'client disposed');
        this.socket = undefined;
        this.pending.clear();
    }
    async open() {
        const token = typeof this.options.token === 'function' ? await this.options.token() : this.options.token;
        const url = token
            ? `${this.options.url}${this.options.url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
            : this.options.url;
        return new Promise((resolve, reject) => {
            const socket = new WebSocket(url, this.options.protocols);
            this.socket = socket;
            socket.onopen = () => {
                this.reconnectAttempts = 0;
                this.outbox.splice(0).forEach((frame) => socket.send(frame));
                this.startHeartbeat();
                resolve();
            };
            socket.onmessage = (event) => this.receive(event.data);
            socket.onerror = () => reject(new Error('WebSocket error'));
            socket.onclose = () => {
                clearInterval(this.heartbeat);
                if (!this.disposed)
                    this.scheduleReconnect();
            };
        });
    }
    scheduleReconnect() {
        const ceiling = this.options.maxReconnectMs ?? 30_000;
        const delay = Math.min(ceiling, 500 * 2 ** this.reconnectAttempts++);
        setTimeout(() => {
            if (this.disposed)
                return;
            this.open().catch(() => {
                this.emit?.({
                    type: 'message.error',
                    error: chatError('network', 'Reconnecting…', { retryable: true }),
                });
            });
        }, delay);
    }
    startHeartbeat() {
        const interval = this.options.heartbeatMs ?? 25_000;
        clearInterval(this.heartbeat);
        this.heartbeat = setInterval(() => {
            if (this.socket?.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ type: 'ping' }));
            }
        }, interval);
    }
    receive(data) {
        let frame;
        try {
            frame = JSON.parse(data);
        }
        catch {
            return;
        }
        // Resolve an in-flight RPC first.
        const id = frame.id;
        if (id && this.pending.has(id)) {
            const entry = this.pending.get(id);
            this.pending.delete(id);
            if (frame.error)
                entry.reject(frame.error);
            else
                entry.resolve(frame.payload ?? frame);
            return;
        }
        const events = this.options.parseFrame?.(frame) ?? defaultParseFrame(frame);
        events.forEach((event) => this.emit?.(event));
    }
    rpc(type, payload) {
        const id = createId('req');
        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            this.send({ id, type, payload });
            setTimeout(() => {
                if (this.pending.delete(id))
                    reject(new Error(`${type} timed out`));
            }, 15_000);
        });
    }
    send(frame) {
        const encoded = JSON.stringify(frame);
        if (this.socket?.readyState === WebSocket.OPEN)
            this.socket.send(encoded);
        else
            this.outbox.push(encoded); // flushed on reconnect
    }
}
/** Default server-frame mapping; replace via `options.parseFrame`. */
function defaultParseFrame(frame) {
    switch (frame.type) {
        case 'message.start':
            return [{ type: 'message.start', message: frame.message }];
        case 'message.delta':
            return [
                {
                    type: 'message.delta',
                    messageId: String(frame.messageId),
                    delta: String(frame.delta ?? frame.text ?? ''),
                },
            ];
        case 'message.sources':
            return [{ type: 'message.sources', messageId: String(frame.messageId), sources: frame.sources }];
        case 'message.suggestions':
            return [
                { type: 'message.suggestions', messageId: String(frame.messageId), suggestions: frame.suggestions },
            ];
        case 'message.actions':
            return [{ type: 'message.actions', messageId: String(frame.messageId), actions: frame.actions }];
        case 'message.complete':
            return [{ type: 'message.complete', messageId: String(frame.messageId) }];
        case 'typing':
            return [{ type: 'typing', isTyping: Boolean(frame.isTyping) }];
        case 'conversation.update':
            return [{ type: 'conversation.update', conversation: frame.conversation }];
        default:
            return [];
    }
}
